import React, { useEffect, useRef, useCallback, useState } from "react";
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from "vexflow";
import type { PracticeNoteEvent } from "../../lib/practiceNoteModel";
import type { NoteName } from "../../lib/guitarEngine";
import {
  PRACTICE_STAFF_GRAND_SCALE,
  PRACTICE_STAFF_HEIGHT_GRAND_LIGHT,
  PRACTICE_STAFF_MIN_GRAND_LIGHT,
} from "../../lib/practiceStaffLayout";
import {
  PRACTICE_STAFF_COLORS,
  PRACTICE_STAFF_CONTAINER_STYLE,
  notationNoteStyle,
  paintStaveLines,
} from "../../lib/practiceStaffTheme";
import { degreeRoman } from "../../lib/scaleDegreeLabels";
import { degreeColor } from "../../lib/practiceVisualTheme";

export interface GrandStaffVisualizerProps {
  trebleNotes?: PracticeNoteEvent[];
  bassNotes?: PracticeNoteEvent[];
  activeMidi?: number | null;
  /** Grado seleccionado desde la partitura (anillo extra). */
  selectedDegree?: string | null;
  onNoteSelect?: (event: PracticeNoteEvent) => void;
  className?: string;
  /** @deprecated Siempre lienzo transparente estilo Canciones. */
  variant?: "dark" | "light";
}

const SCALE = PRACTICE_STAFF_GRAND_SCALE;

function buildVexKey(note: NoteName, octave: number): string {
  const baseName = note.replace("#", "").replace("b", "").toLowerCase();
  return `${baseName}/${octave}`;
}

function eventsToStaveNotes(
  events: PracticeNoteEvent[],
  activeMidi: number | null,
  selectedDegree: string | null
): InstanceType<typeof StaveNote>[] {
  return events.map((ev) => {
    const octave = Math.floor(ev.midi / 12) - 1;
    const step = ev.step ?? ("C" as NoteName);
    const staveNote = new StaveNote({
      keys: [buildVexKey(step, octave)],
      duration: "q",
      autoStem: true,
    });
    if (step.includes("#")) staveNote.addModifier(new Accidental("#"), 0);
    else if (step.includes("b") && step.length > 1) staveNote.addModifier(new Accidental("b"), 0);

    const isActive = activeMidi === ev.midi;
    const isDegreeSelected = selectedDegree != null && ev.degree === selectedDegree;
    staveNote.setStyle(
      notationNoteStyle({
        active: isActive,
        selected: isDegreeSelected,
      })
    );
    return staveNote;
  });
}

type HitTarget = {
  event: PracticeNoteEvent;
  x: number;
  y: number;
  w: number;
  h: number;
};

function drawVoiceOnStave(
  ctx: ReturnType<Renderer["getContext"]>,
  stave: Stave,
  events: PracticeNoteEvent[],
  activeMidi: number | null,
  selectedDegree: string | null,
  formatWidth: number,
  hitsOut: HitTarget[]
) {
  if (events.length === 0) return;

  const slice = events.slice(0, 12);
  const vexNotes = eventsToStaveNotes(slice, activeMidi, selectedDegree);
  const voice = new Voice({ numBeats: vexNotes.length, beatValue: 4 });
  voice.addTickables(vexNotes);
  new Formatter().joinVoices([voice]).format([voice], formatWidth);

  const tickables = voice.getTickables() as InstanceType<typeof StaveNote>[];
  tickables.forEach((t) => t.setStave(stave));
  voice.draw(ctx, stave);

  tickables.forEach((tickable, i) => {
    const ev = slice[i];
    if (!ev) return;
    try {
      const bbox = tickable.getBoundingBox();
      if (!bbox) return;
      const x = bbox.getX();
      const y = bbox.getY();
      const w = bbox.getW();
      const h = bbox.getH();

      hitsOut.push({
        event: ev,
        x: x * SCALE,
        y: y * SCALE,
        w: Math.max(w * SCALE, 22),
        h: Math.max(h * SCALE, 28),
      });
    } catch {
      /* ignore */
    }
  });
}

function StaffDegreeLabels({
  hits,
  selectedDegree,
  activeMidi,
}: {
  hits: HitTarget[];
  selectedDegree: string | null;
  activeMidi: number | null;
}) {
  return (
    <div className="absolute inset-0 pointer-events-none z-[1]" aria-hidden>
      {hits.map((h) => {
        const ev = h.event;
        if (!ev.degree) return null;
        const deg = ev.degree.replace("*", "");
        const color = degreeColor(deg);
        const roman = degreeRoman(ev.degree);
        const noteName = ev.step ?? "";
        const isDegreeSelected = selectedDegree != null && ev.degree === selectedDegree;
        const isActive = activeMidi === ev.midi;
        const cx = h.x + h.w / 2;

        return (
          <div
            key={`${ev.midi}-${ev.degree}-${cx}`}
            className="absolute flex flex-col items-center"
            style={{ left: cx, top: h.y }}
          >
            <span
              className="-translate-x-1/2 -translate-y-[calc(100%+6px)] absolute flex flex-col items-center gap-0.5"
            >
              <span
                className={`inline-flex items-center justify-center min-w-[1.35rem] px-1.5 py-0.5 rounded-full text-[10px] font-serif font-semibold tracking-[0.08em] border backdrop-blur-sm transition-shadow duration-200 ${
                  isDegreeSelected ? "ring-1 ring-offset-0" : ""
                }`}
                style={{
                  color,
                  borderColor: `${color}${isDegreeSelected ? "aa" : "55"}`,
                  background: `linear-gradient(180deg, ${color}28 0%, ${color}12 100%)`,
                  boxShadow: isDegreeSelected
                    ? `0 0 12px ${color}44, 0 1px 0 rgba(255,255,255,0.06) inset`
                    : isActive
                      ? `0 0 8px ${color}33`
                      : "0 1px 0 rgba(255,255,255,0.04) inset",
                  ...(isDegreeSelected ? { ringColor: `${color}66` } : {}),
                }}
              >
                {roman}
              </span>
            </span>
            {noteName && (
              <span
                className="absolute -translate-x-1/2 text-[9px] font-sans font-medium tracking-wide text-slate-500/90"
                style={{ top: h.h + 4 }}
              >
                {noteName}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export const GrandStaffVisualizer: React.FC<GrandStaffVisualizerProps> = ({
  trebleNotes = [],
  bassNotes = [],
  activeMidi = null,
  selectedDegree = null,
  onNoteSelect,
  className = "",
  variant = "dark",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgHostRef = useRef<HTMLDivElement>(null);
  const hitsRef = useRef<HitTarget[]>([]);
  const [labelHits, setLabelHits] = useState<HitTarget[]>([]);

  const renderScore = useCallback(() => {
    const host = svgHostRef.current;
    if (!host) return;
    host.innerHTML = "";
    hitsRef.current = [];

    const width = host.clientWidth || containerRef.current?.clientWidth || 720;
    const height = PRACTICE_STAFF_HEIGHT_GRAND_LIGHT;
    const renderer = new Renderer(host, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const ctx = renderer.getContext();
    ctx.scale(SCALE, SCALE);

    const pad = 16;
    const staveW = (width - pad * 2) / SCALE;
    const formatWidth = staveW - 50;

    const treble = new Stave(pad / SCALE, 8, staveW);
    treble.addClef("treble");
    paintStaveLines(ctx);
    treble.setContext(ctx).draw();

    const bass = new Stave(pad / SCALE, 88, staveW);
    bass.addClef("bass");
    paintStaveLines(ctx);
    bass.setContext(ctx).draw();

    const braceX = pad / SCALE - 8;
    ctx.setStrokeStyle(PRACTICE_STAFF_COLORS.brace);
    ctx.setLineWidth(1.5);
    ctx.beginPath();
    ctx.moveTo(braceX, 12);
    ctx.lineTo(braceX, 148);
    ctx.stroke();

    drawVoiceOnStave(ctx, treble, trebleNotes, activeMidi, selectedDegree, formatWidth, hitsRef.current);
    drawVoiceOnStave(ctx, bass, bassNotes, activeMidi, selectedDegree, formatWidth, hitsRef.current);

    setLabelHits(
      hitsRef.current.filter((h) => h.event.degree).map((h) => ({ ...h }))
    );
  }, [trebleNotes, bassNotes, activeMidi, selectedDegree]);

  useEffect(() => {
    renderScore();
  }, [renderScore]);

  useEffect(() => {
    const host = svgHostRef.current;
    if (!host) return;
    const ro = new ResizeObserver(() => renderScore());
    ro.observe(host);
    return () => ro.disconnect();
  }, [renderScore]);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onNoteSelect || hitsRef.current.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    let best: HitTarget | null = null;
    let bestDist = Infinity;
    for (const h of hitsRef.current) {
      const cx = h.x + h.w / 2;
      const cy = h.y + h.h / 2;
      const d = (x - cx) ** 2 + (y - cy) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = h;
      }
    }
    if (best && bestDist < 80 ** 2) onNoteSelect(best.event);
  };

  const hasDegreeLabels = labelHits.length > 0;

  return (
    <div
      ref={containerRef}
      className={`relative w-full overflow-hidden rounded-lg ${className}`}
      style={{
        ...PRACTICE_STAFF_CONTAINER_STYLE,
        minHeight: PRACTICE_STAFF_MIN_GRAND_LIGHT,
      }}
      aria-label="Gran pentagrama — clic en una nota para resaltar el grado"
    >
      <div ref={svgHostRef} className="w-full" />
      {hasDegreeLabels && (
        <StaffDegreeLabels
          hits={labelHits}
          selectedDegree={selectedDegree}
          activeMidi={activeMidi}
        />
      )}
      {onNoteSelect && (
        <div
          className="absolute inset-0 cursor-pointer"
          style={{ zIndex: 2 }}
          onClick={handleOverlayClick}
          role="presentation"
          title="Clic en una nota para resaltar su grado"
        />
      )}
    </div>
  );
};
